import React, { useState, useEffect } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { 
  Mail, Calendar, User, Paperclip, Download, Link2, Brain,
  AlertTriangle, Clock, FileText, Building2, Tag, ChevronDown,
  ExternalLink, Copy, Target, Loader2, CheckCircle, Send, Users, FileDown
} from 'lucide-react';

const EmailDetailModal = ({ email: initialEmail, onClose, onUpdate, projectId }) => {
  const [email, setEmail] = useState(initialEmail);
  const [linking, setLinking] = useState(false);
  const [linkModule, setLinkModule] = useState('');
  const [linkEntityId, setLinkEntityId] = useState('');
  const [moduleOptions, setModuleOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [extractingDates, setExtractingDates] = useState(false);
  
  // Distribution state
  const [showDistribute, setShowDistribute] = useState(false);
  const [distributing, setDistributing] = useState(false);
  const [distributionOptions, setDistributionOptions] = useState({ internal_team: [], professional_team: [] });
  const [selectedInternal, setSelectedInternal] = useState([]);
  const [selectedExternal, setSelectedExternal] = useState([]);
  const [additionalEmails, setAdditionalEmails] = useState('');
  const [distributionNote, setDistributionNote] = useState('');

  // Create RFI state
  const [showCreateRFI, setShowCreateRFI] = useState(false);
  const [creatingRFI, setCreatingRFI] = useState(false);
  const [rfiDiscipline, setRfiDiscipline] = useState('General');
  const [rfiPriority, setRfiPriority] = useState('Medium');
  const [rfiTitle, setRfiTitle] = useState('');
  const [rfiQuery, setRfiQuery] = useState('');
  const [rfiSelectedAttachments, setRfiSelectedAttachments] = useState([]);
  const [rfiResponseDays, setRfiResponseDays] = useState(7);

  // Determine if email is already linked to a delay event
  const isLinkedToDelayEvent = email.module_type === 'DelayEvent' && email.module_entity_id;

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Load module options when module type changes
  useEffect(() => {
    if (!linkModule || !projectId) {
      setModuleOptions([]);
      return;
    }

    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        let options = [];
        
        if (linkModule === 'RFI') {
          const response = await api.get(`/projects/${projectId}/rfis`);
          options = (response.data.rfis || response.data || []).map(r => ({
            id: r.id,
            label: `${r.reference || `RFI-${r.number}`} - ${r.subject?.substring(0, 40) || 'No subject'}...`
          }));
        } else if (linkModule === 'SI') {
          const response = await api.get(`/projects/${projectId}/site-instructions`);
          options = (response.data.site_instructions || response.data || []).map(s => ({
            id: s.id,
            label: `${s.reference || `SI-${s.number}`} - ${s.title?.substring(0, 40) || 'No title'}...`
          }));
        } else if (linkModule === 'Notice') {
          const response = await api.get(`/projects/${projectId}/notices`);
          options = (response.data.notices || response.data || []).map(n => ({
            id: n.id,
            label: `${n.reference || `Notice-${n.number}`} - ${n.title?.substring(0, 40) || 'No title'}...`
          }));
        } else if (linkModule === 'Claim') {
          const response = await api.get(`/projects/${projectId}/claims`);
          options = (response.data.claims || response.data || []).map(c => ({
            id: c.id,
            label: `${c.reference || `Claim-${c.number}`} - ${c.title?.substring(0, 40) || 'No title'}...`
          }));
        } else if (linkModule === 'DelayEvent') {
          const response = await api.get(`/projects/${projectId}/events`);
          options = (response.data.events || response.data || []).map(e => ({
            id: e.id,
            label: `${e.event_type || e.title || 'Delay Event'} - ${e.narrative?.substring(0, 30) || ''}...`
          }));
        }
        
        setModuleOptions(options);
      } catch (error) {
        console.error('Failed to load module options:', error);
        setModuleOptions([]);
      } finally {
        setLoadingOptions(false);
      }
    };

    loadOptions();
  }, [linkModule, projectId]);

  const handleLink = async () => {
    if (!linkModule) {
      toast.error('Please select a module type');
      return;
    }
    if (!linkEntityId) {
      toast.error('Please select which item to link to');
      return;
    }
    
    setLinking(true);
    try {
      await api.put(`/correspondence/emails/${email.id}`, {
        module_type: linkModule,
        module_entity_id: linkEntityId
      });
      toast.success('Email linked successfully');
      onUpdate();
    } catch (error) {
      toast.error('Failed to link email');
    } finally {
      setLinking(false);
    }
  };

  const triggerAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await api.post(`/correspondence/emails/${email.id}/analyze`);
      setEmail(response.data);
      toast.success('AI analysis complete');
      onUpdate();
    } catch (error) {
      toast.error('Failed to analyze email');
    } finally {
      setAnalyzing(false);
    }
  };

  const extractDatesToTimeline = async () => {
    setExtractingDates(true);
    try {
      const response = await api.post(`/correspondence/emails/${email.id}/extract-dates`);
      const { dates, message } = response.data;
      
      if (dates && dates.length > 0) {
        toast.success(message || `Extracted ${dates.length} dates to timeline`);
        // Show extracted dates summary
        const datesList = dates.map(d => `• ${d.date}: ${d.description}`).join('\n');
        console.log('Extracted dates:', datesList);
      } else {
        toast.info('No dates found in this email');
      }
      
      setEmail(prev => ({
        ...prev,
        dates_extracted: true,
        extracted_dates_count: dates?.length || 0
      }));
      onUpdate();
    } catch (error) {
      toast.error('Failed to extract dates');
    } finally {
      setExtractingDates(false);
    }
  };

  const openDistributeDialog = async () => {
    setShowDistribute(true);
    try {
      const response = await api.get(`/correspondence/emails/${email.id}/distribution-options`);
      setDistributionOptions(response.data);
    } catch (error) {
      toast.error('Failed to load team members');
    }
  };

  const handleDistribute = async () => {
    const additionalEmailList = additionalEmails
      .split(/[,;\n]/)
      .map(e => e.trim())
      .filter(e => e && e.includes('@'));
    
    if (selectedInternal.length === 0 && selectedExternal.length === 0 && additionalEmailList.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }
    
    setDistributing(true);
    try {
      const response = await api.post(`/correspondence/emails/${email.id}/distribute`, {
        internal_recipients: selectedInternal,
        external_recipients: selectedExternal,
        additional_emails: additionalEmailList,
        note: distributionNote
      });
      
      toast.success(response.data.message);
      setShowDistribute(false);
      setSelectedInternal([]);
      setSelectedExternal([]);
      setAdditionalEmails('');
      setDistributionNote('');
      onUpdate();
    } catch (error) {
      toast.error('Failed to distribute email');
    } finally {
      setDistributing(false);
    }
  };

  const openCreateRFI = () => {
    setRfiTitle(email.subject || '');
    setRfiQuery('');
    // Pre-select all attachments
    const attachmentIds = (email.attachments || []).map(att => att.id || att.filename);
    setRfiSelectedAttachments(attachmentIds);
    setShowCreateRFI(true);
  };

  const handleCreateRFI = async () => {
    setCreatingRFI(true);
    try {
      const response = await api.post(`/correspondence/emails/${email.id}/create-rfi`, {
        discipline: rfiDiscipline,
        priority: rfiPriority,
        title: rfiTitle || null,
        query: rfiQuery || null,
        required_response_days: rfiResponseDays,
        selected_attachments: rfiSelectedAttachments
      });
      
      toast.success(response.data.message);
      setShowCreateRFI(false);
      
      // Ask if user wants to view the RFI
      if (response.data.rfi?.id) {
        const viewRFI = window.confirm(`RFI ${response.data.rfi.rfi_number} created. Open it now?`);
        if (viewRFI) {
          window.open(`/projects/${response.data.rfi.project_id}/rfi/${response.data.rfi.id}`, '_blank');
        }
      }
      
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create RFI');
    } finally {
      setCreatingRFI(false);
    }
  };

  const toggleRecipient = (id, type) => {
    if (type === 'internal') {
      setSelectedInternal(prev => 
        prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
      );
    } else {
      setSelectedExternal(prev => 
        prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
      );
    }
  };

  const toggleRfiAttachment = (attId) => {
    setRfiSelectedAttachments(prev => 
      prev.includes(attId) ? prev.filter(i => i !== attId) : [...prev, attId]
    );
  };

  const downloadOriginal = async () => {
    try {
      // Check if this is an email or manual entry
      const isManualEntry = email._source === 'manual' || !email.original_filename;
      
      if (isManualEntry) {
        // For manual entries, generate a text file with the content
        const content = `Subject: ${email.subject || 'No Subject'}
From: ${email.from_name || ''} <${email.from_email || ''}>
To: ${Array.isArray(email.to) ? email.to.join(', ') : email.to || ''}
Date: ${email.sent_at || email.created_at || ''}

${email.body_plain || email.body_clean || email.body_raw || ''}`;
        
        const blob = new Blob([content], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `correspondence_${email.id?.slice(0, 8) || 'entry'}.txt`;
        link.click();
        window.URL.revokeObjectURL(url);
        toast.success('Correspondence downloaded');
      } else {
        // For imported emails, try to download original .eml file
        const response = await api.get(`/correspondence/emails/${email.id}/download`, {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = email.original_filename || 'email.eml';
        link.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error('Failed to download email');
    }
  };

  const downloadAsMsg = async () => {
    try {
      // Generate .msg compatible format (simplified - this creates an .eml which Outlook can open)
      // Note: True .msg format requires binary OLE compound document format which is complex
      // Creating .eml format which is universally supported
      const headers = [
        `From: ${email.from_name ? `"${email.from_name}" <${email.from_email}>` : email.from_email || ''}`,
        `To: ${Array.isArray(email.to) ? email.to.join(', ') : email.to || ''}`,
        `Subject: ${email.subject || 'No Subject'}`,
        `Date: ${email.sent_at || email.created_at || new Date().toISOString()}`,
        `MIME-Version: 1.0`,
        `Content-Type: text/plain; charset=utf-8`,
        `Content-Transfer-Encoding: 8bit`,
        ``
      ].join('\r\n');
      
      const body = email.body_plain || email.body_clean || email.body_raw || '';
      const emlContent = headers + body;
      
      const blob = new Blob([emlContent], { type: 'message/rfc822' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Use .eml extension which Outlook can open natively
      const filename = (email.subject || 'email').replace(/[^a-zA-Z0-9]/g, '_').slice(0, 50);
      link.download = `${filename}.eml`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Email downloaded as .eml (Outlook compatible)');
    } catch (error) {
      toast.error('Failed to download email');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getImpactColor = (score) => {
    if (score >= 70) return 'text-red-600 bg-red-50';
    if (score >= 40) return 'text-amber-600 bg-amber-50';
    return 'text-green-600 bg-green-50';
  };

  const getModuleLink = (moduleType, entityId) => {
    const links = {
      'RFI': `/rfis/${entityId}`,
      'SI': `/site-instructions/${entityId}`,
      'DelayEvent': `/events/${entityId}`,
      'Notice': `/notices/${entityId}`,
      'Claim': `/claims/${entityId}`,
      'Drawing': `/drawings/${entityId}`
    };
    return links[moduleType] || '#';
  };

  return (
    <>
      {/* Main Email Detail Dialog */}
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto z-[50]" overlayClassName="z-[40]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-indigo-600" />
              Email Details
            </DialogTitle>
          </DialogHeader>

          {/* Linked Modules Summary */}
          {email.module_entity_id && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <CheckCircle className="h-4 w-4 text-indigo-600 shrink-0" />
                <span className="text-sm text-indigo-800 truncate">
                  Linked to <strong>{email.module_type}</strong>
                  {email.module_entity_reference && `: ${email.module_entity_reference}`}
                </span>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              className="text-indigo-600 border-indigo-300 shrink-0"
              onClick={() => window.open(getModuleLink(email.module_type, email.module_entity_id), '_blank')}
            >
              <ExternalLink className="h-3 w-3 sm:mr-1" />
              <span className="hidden sm:inline">View</span>
            </Button>
          </div>
        )}

        <Tabs defaultValue="content" className="w-full">
          <div className="overflow-x-auto -mx-2 px-2">
            <TabsList className="inline-flex w-auto min-w-full justify-start">
              <TabsTrigger value="content" className="text-xs sm:text-sm">Content</TabsTrigger>
              <TabsTrigger value="analysis" className="text-xs sm:text-sm">AI Analysis</TabsTrigger>
              <TabsTrigger value="attachments" className="text-xs sm:text-sm whitespace-nowrap">
                Attachments ({email.attachments?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="link" className="text-xs sm:text-sm whitespace-nowrap">Link & Actions</TabsTrigger>
            </TabsList>
          </div>

          {/* Content Tab */}
          <TabsContent value="content" className="space-y-4">
            {/* Header info */}
            <div className="bg-slate-50 rounded-lg p-3 sm:p-4 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-semibold text-base sm:text-lg text-slate-900 break-words">{email.subject}</h3>
                  <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                    <Calendar className="h-4 w-4 shrink-0" />
                    <span className="truncate">{formatDate(email.sent_at)}</span>
                  </p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="shrink-0 self-start">
                      <Download className="h-4 w-4 sm:mr-2" />
                      <span className="hidden sm:inline">Download</span>
                      <ChevronDown className="h-3 w-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={downloadOriginal}>
                      <FileText className="h-4 w-4 mr-2" />
                      Original Format
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={downloadAsMsg}>
                      <FileDown className="h-4 w-4 mr-2" />
                      Outlook (.eml)
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
                <div className="min-w-0">
                  <p className="text-slate-500">From:</p>
                  <p className="font-medium break-all">
                    {email.from_name && <span>{email.from_name} </span>}
                    <span className="text-slate-600">&lt;{email.from_email}&gt;</span>
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="text-slate-500">To:</p>
                  <p className="font-medium text-slate-600 break-all">
                    {Array.isArray(email.to) 
                      ? email.to.map((t, i) => (
                          <span key={i}>
                            {typeof t === 'object' ? `${t.name || ''} <${t.email}>` : t}
                            {i < email.to.length - 1 && ', '}
                          </span>
                        ))
                      : email.to
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="border rounded-lg p-3 sm:p-4 overflow-hidden">
              <div className="prose prose-sm max-w-none overflow-x-auto">
                <pre className="whitespace-pre-wrap break-words font-sans text-sm text-slate-700">
                  {email.body_raw || email.body_clean || email.body_plain}
                </pre>
              </div>
            </div>
          </TabsContent>

          {/* Analysis Tab */}
          <TabsContent value="analysis" className="space-y-4">
            {/* AI Analysis Button */}
            <div className="flex justify-end">
              <Button 
                onClick={triggerAnalysis} 
                disabled={analyzing}
                variant={email.ai_analyzed ? "outline" : "default"}
              >
                {analyzing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Brain className="h-4 w-4 mr-2" />
                    {email.ai_analyzed ? 'Re-analyze' : 'Run AI Analysis'}
                  </>
                )}
              </Button>
            </div>

            {analyzing && (
              <div className="text-center py-8">
                <Loader2 className="h-12 w-12 mx-auto mb-3 animate-spin text-indigo-600" />
                <p className="text-slate-600">Analyzing email content...</p>
                <p className="text-sm text-slate-400 mt-1">This may take a few seconds</p>
              </div>
            )}

            {!analyzing && !email.ai_analyzed && (
              <div className="text-center py-8 text-slate-400">
                <Brain className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>AI analysis not yet performed</p>
                <p className="text-sm mt-1">Click the button above to analyze this email</p>
              </div>
            )}
            
            {!analyzing && email.ai_analyzed && (
              <>
                {/* Summary */}
                {email.ai_summary && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                      <Brain className="h-4 w-4" />
                      AI Summary
                    </h4>
                    <p className="text-slate-600">{email.ai_summary}</p>
                  </div>
                )}

                {/* Impact Assessment */}
                {email.ai_impact && (
                  <div className={`rounded-lg p-4 ${getImpactColor(email.ai_impact.delay_risk_score || 0)}`}>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Impact Assessment
                    </h4>
                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                      <div className="bg-white/50 rounded p-2 text-center">
                        <p className="text-xs opacity-75">Risk Score</p>
                        <p className="text-2xl font-bold">{email.ai_impact.delay_risk_score || 0}</p>
                      </div>
                      <div className="bg-white/50 rounded p-2 text-center">
                        <p className="text-xs opacity-75">Affects Time</p>
                        <p className="font-medium">{email.ai_impact.affects_time ? 'Yes' : 'No'}</p>
                      </div>
                      <div className="bg-white/50 rounded p-2 text-center">
                        <p className="text-xs opacity-75">Affects Cost</p>
                        <p className="font-medium">{email.ai_impact.affects_cost ? 'Yes' : 'No'}</p>
                      </div>
                    </div>
                    
                    {email.ai_impact.recommended_action && (
                      <div className="bg-white/50 rounded p-2 mt-3">
                        <p className="text-xs opacity-75">Recommended Action</p>
                        <p className="font-medium">{email.ai_impact.recommended_action}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Tags */}
                {email.ai_tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {email.ai_tags.map((tag, idx) => (
                      <Badge key={idx} variant="secondary">
                        <Tag className="h-3 w-3 mr-1" />
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Attachments Tab */}
          <TabsContent value="attachments" className="space-y-2">
            {email.attachments?.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Paperclip className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No attachments</p>
              </div>
            ) : (
              email.attachments?.map((att, idx) => (
                <div 
                  key={att.id || idx}
                  className="flex items-center justify-between gap-2 bg-slate-50 rounded-lg p-3"
                >
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-slate-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{att.filename}</p>
                      <p className="text-xs text-slate-500">
                        {att.file_type || 'File'} {att.file_size ? `• ${Math.round(att.file_size / 1024)}KB` : ''}
                      </p>
                    </div>
                  </div>
                  {att.storage_path && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await api.get(`/correspondence/attachments/${att.id}/download`, {
                            responseType: 'blob'
                          });
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = att.filename;
                          link.click();
                        } catch (error) {
                          toast.error('Download failed');
                        }
                      }}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))
            )}
          </TabsContent>

          {/* Link Tab */}
          <TabsContent value="link" className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="font-medium text-slate-700 mb-3">Link to Module</h4>
              
              {/* Current linkage info */}
              {email.module_entity_id && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                  <p className="text-green-800">
                    <CheckCircle className="h-4 w-4 inline mr-1" />
                    Currently linked to <strong>{email.module_type}</strong>
                    {email.module_entity_reference && `: ${email.module_entity_reference}`}
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500 mb-2">Module Type</p>
                  <Select value={linkModule} onValueChange={(val) => { setLinkModule(val); setLinkEntityId(''); }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select module type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="RFI">RFI</SelectItem>
                      <SelectItem value="SI">Site Instruction</SelectItem>
                      <SelectItem value="Notice">Notice</SelectItem>
                      <SelectItem value="Claim">Claim</SelectItem>
                      {/* Only show Delay Event option if not already linked to one */}
                      {!isLinkedToDelayEvent && (
                        <SelectItem value="DelayEvent">Delay Event</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-2">Select Item</p>
                  <Select 
                    value={linkEntityId} 
                    onValueChange={setLinkEntityId}
                    disabled={!linkModule || loadingOptions}
                  >
                    <SelectTrigger>
                      {loadingOptions ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading...
                        </span>
                      ) : (
                        <SelectValue placeholder={linkModule ? "Select item" : "Select module type first"} />
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      {moduleOptions.length === 0 && !loadingOptions && linkModule && (
                        <div className="p-2 text-sm text-slate-500">No {linkModule}s found</div>
                      )}
                      {moduleOptions.map((opt) => (
                        <SelectItem key={opt.id} value={opt.id}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Button 
                className="mt-4" 
                onClick={handleLink} 
                disabled={linking || !linkModule || !linkEntityId}
              >
                {linking ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Linking...
                  </>
                ) : (
                  <>
                    <Link2 className="h-4 w-4 mr-2" />
                    Link Email
                  </>
                )}
              </Button>
            </div>

            {/* Copy & Download Actions */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="font-medium text-slate-700 mb-3">Copy & Export</h4>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => copyToClipboard(email.subject)}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Subject
                </Button>
                <Button variant="outline" size="sm" onClick={() => copyToClipboard(email.body_clean || email.body_plain || '')}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Body
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Download
                      <ChevronDown className="h-3 w-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={downloadOriginal}>
                      <FileText className="h-4 w-4 mr-2" />
                      Original Format
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={downloadAsMsg}>
                      <FileDown className="h-4 w-4 mr-2" />
                      Outlook (.eml)
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Current Status */}
            <div className="border rounded-lg p-4">
              <h4 className="font-medium text-slate-700 mb-3">Current Status</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Module Type</p>
                  <p className="font-medium">{email.module_type || 'Unassigned'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Linked Entity</p>
                  <p className="font-medium">{email.module_entity_reference || 'None'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Imported</p>
                  <p className="font-medium">{formatDate(email.imported_at || email.created_at)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Source</p>
                  <p className="font-medium">{email.source || 'Unknown'}</p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>

    {/* Distribute Dialog - Rendered outside main dialog */}
    {showDistribute && (
      <Dialog open={showDistribute} onOpenChange={setShowDistribute}>
        <DialogContent className="max-w-xl z-[100] mx-2 sm:mx-auto" overlayClassName="z-[90]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-indigo-600" />
              Distribute to Team
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-5">
              {/* Internal Team */}
              {/* Internal Team */}
              <div>
                <h4 className="font-medium text-sm text-slate-700 mb-3">Internal Team</h4>
                <div className="max-h-40 overflow-y-auto border rounded-lg p-3 space-y-2 bg-slate-50">
                  {distributionOptions.internal_team?.length > 0 ? (
                    distributionOptions.internal_team.map(member => (
                      <label 
                        key={member.id} 
                        className="flex items-center gap-3 p-3 bg-white hover:bg-blue-50 rounded-lg cursor-pointer border border-slate-200 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedInternal.includes(member.id)}
                          onChange={() => toggleRecipient(member.id, 'internal')}
                          className="rounded border-slate-300 h-4 w-4"
                        />
                        <span className="text-sm font-medium">{member.name}</span>
                        <span className="text-xs text-slate-500">{member.role}</span>
                        <span className="text-xs text-slate-400 ml-auto">{member.email}</span>
                      </label>
                    ))
                  ) : (
                    <p className="text-sm text-slate-400 p-3">No internal team members in this project</p>
                  )}
                </div>
              </div>
              
              {/* Professional Team */}
              <div>
                <h4 className="font-medium text-sm text-slate-700 mb-3">Professional Team</h4>
                <div className="max-h-40 overflow-y-auto border rounded-lg p-3 space-y-2 bg-slate-50">
                  {distributionOptions.professional_team?.length > 0 ? (
                    distributionOptions.professional_team.map(member => (
                      <label 
                        key={member.id} 
                        className="flex items-center gap-3 p-3 bg-white hover:bg-blue-50 rounded-lg cursor-pointer border border-slate-200 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedExternal.includes(member.id)}
                          onChange={() => toggleRecipient(member.id, 'external')}
                          className="rounded border-slate-300 h-4 w-4"
                        />
                        <span className="text-sm font-medium">{member.name || member.company}</span>
                        <span className="text-xs text-slate-500">{member.discipline}</span>
                        <span className="text-xs text-slate-400 ml-auto">{member.email}</span>
                      </label>
                    ))
                  ) : (
                    <p className="text-sm text-slate-400 p-3">No professional team members in this project</p>
                  )}
                </div>
              </div>
              
              {/* Additional Emails */}
              <div>
                <h4 className="font-medium text-sm text-slate-700 mb-3">Additional Email Addresses</h4>
                <textarea
                  value={additionalEmails}
                  onChange={(e) => setAdditionalEmails(e.target.value)}
                  placeholder="Enter email addresses (comma or newline separated)"
                  className="w-full border rounded-lg p-3 text-sm h-24 resize-none"
                />
              </div>
              
              {/* Note */}
              <div>
                <h4 className="font-medium text-sm text-slate-700 mb-3">Add a Note (optional)</h4>
                <textarea
                  value={distributionNote}
                  onChange={(e) => setDistributionNote(e.target.value)}
                  placeholder="Add a note to include with the distribution..."
                  className="w-full border rounded-lg p-3 text-sm h-24 resize-none"
                />
              </div>
              
              {/* Summary */}
              <div className="bg-blue-50 rounded-lg p-4 text-sm border border-blue-100">
                <p className="text-slate-700">
                  <strong className="text-blue-600">
                    {selectedInternal.length + selectedExternal.length + 
                      (additionalEmails.split(/[,;\n]/).filter(e => e.trim() && e.includes('@')).length)}
                  </strong> recipient(s) selected
                </p>
              </div>
              
              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowDistribute(false)}>
                  Cancel
                </Button>
                <Button onClick={handleDistribute} disabled={distributing} className="min-w-[120px]">
                  {distributing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Distribute
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Create RFI Dialog - Rendered outside main dialog */}
      {showCreateRFI && (
        <Dialog open={showCreateRFI} onOpenChange={setShowCreateRFI}>
          <DialogContent className="max-w-2xl z-[100] mx-2 sm:mx-auto" overlayClassName="z-[90]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-indigo-600" />
                Create RFI from Email
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-2">
              {/* Title */}
              <div>
                <label className="font-medium text-sm text-slate-700 mb-2 block">RFI Title</label>
                <input
                  type="text"
                  value={rfiTitle}
                  onChange={(e) => setRfiTitle(e.target.value)}
                  placeholder="Enter RFI title (or leave empty to use email subject)"
                  className="w-full border rounded-lg p-3 text-sm"
                />
              </div>
              
              {/* Discipline & Priority */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="font-medium text-sm text-slate-700 mb-2 block">Discipline</label>
                  <Select value={rfiDiscipline} onValueChange={setRfiDiscipline}>
                    <SelectTrigger className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[110]">
                      <SelectItem value="General">General</SelectItem>
                      <SelectItem value="Architectural">Architectural</SelectItem>
                      <SelectItem value="Structural">Structural</SelectItem>
                      <SelectItem value="Mechanical">Mechanical</SelectItem>
                      <SelectItem value="Electrical">Electrical</SelectItem>
                      <SelectItem value="Civil">Civil</SelectItem>
                      <SelectItem value="Plumbing">Plumbing</SelectItem>
                      <SelectItem value="Fire">Fire</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="font-medium text-sm text-slate-700 mb-2 block">Priority</label>
                  <Select value={rfiPriority} onValueChange={setRfiPriority}>
                    <SelectTrigger className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[110]">
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Response Days */}
              <div>
                <label className="font-medium text-sm text-slate-700 mb-2 block">Response Required Within (days)</label>
                <input
                  type="number"
                  min="1"
                  max="90"
                  value={rfiResponseDays}
                  onChange={(e) => setRfiResponseDays(parseInt(e.target.value) || 7)}
                  className="w-full border rounded-lg p-3 text-sm"
                />
              </div>
              
              {/* Additional Query/Notes */}
              <div>
                <label className="font-medium text-sm text-slate-700 mb-2 block">Additional Query/Notes (optional)</label>
                <textarea
                  value={rfiQuery}
                  onChange={(e) => setRfiQuery(e.target.value)}
                  placeholder="Add any additional context or questions (email content will be included automatically)"
                  className="w-full border rounded-lg p-3 text-sm h-28 resize-none"
                />
              </div>
              
              {/* Attachments Selection */}
              {email.attachments?.length > 0 && (
                <div>
                  <label className="font-medium text-sm text-slate-700 mb-3 block">
                    Include Attachments ({rfiSelectedAttachments.length}/{email.attachments.length} selected)
                  </label>
                  <div className="max-h-48 overflow-y-auto border rounded-lg p-3 space-y-2 bg-slate-50">
                    {email.attachments.map((att, idx) => {
                      const attId = att.id || att.filename;
                      return (
                        <label 
                          key={attId || idx}
                          className="flex items-center gap-3 p-3 bg-white hover:bg-blue-50 rounded-lg cursor-pointer border border-slate-200 transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={rfiSelectedAttachments.includes(attId)}
                            onChange={() => toggleRfiAttachment(attId)}
                            className="rounded border-slate-300 h-4 w-4"
                          />
                          <Paperclip className="h-4 w-4 text-slate-500 flex-shrink-0" />
                          <span className="text-sm flex-1 truncate font-medium">{att.filename}</span>
                          <span className="text-xs text-slate-500 flex-shrink-0">
                            {att.file_type || 'File'}
                            {att.file_size ? ` • ${Math.round(att.file_size / 1024)}KB` : ''}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Email Preview */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <p className="text-xs text-blue-600 font-medium mb-2">Source Email:</p>
                <p className="text-sm font-semibold text-slate-800">{email.subject}</p>
                <p className="text-xs text-slate-500 mt-1">From: {email.from_email}</p>
              </div>
            </div>
              
            {/* Actions - Outside scrollable area */}
            <div className="flex justify-end gap-3 pt-4 border-t mt-4">
              <Button variant="outline" onClick={() => setShowCreateRFI(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateRFI} disabled={creatingRFI} className="min-w-[120px]">
                {creatingRFI ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Target className="h-4 w-4 mr-2" />
                    Create RFI
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default EmailDetailModal;
